/**
 * Pre-built reports API client.
 *
 * Wired against PreBuiltReportsController on service-reporting-api:
 *   GET /v1/reports                             — index
 *   GET /v1/reports/daily-transactions          — daily tx report
 *   GET /v1/reports/delivery-sla                — delivery SLA report
 *   GET /v1/reports/fleet-ops                   — fleet ops (stub)
 *   GET /v1/reports/kyc-compliance              — KYC compliance (stub)
 *   GET /v1/reports/audit-compliance            — audit compliance
 *   GET /v1/reports/reconciliation              — reconciliation (stub)
 *   GET /v1/reports/revenue-sharing             — revenue sharing (stub)
 *   GET /v1/reports/staff-productivity          — staff productivity
 *   GET /v1/reports/gov-services-monthly        — gov services monthly
 *   GET /v1/reports/franchise-scorecard         — franchise scorecard
 */
import { env } from '../env';
import { request } from './client';

const apiBase = (): string => env.serviceReportingApiBaseUrl;

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

export interface ReportIndexItem {
  readonly slug: string;
  readonly reportKey: string;
  readonly title: string;
  readonly description: string;
  readonly category: string;
  readonly endpoint: string;
  readonly params: readonly string[];
}

// ---------------------------------------------------------------------------
// Daily Transactions
// ---------------------------------------------------------------------------

export interface DailyTransactionRow {
  readonly category: string;
  readonly transactionCount: number;
  readonly totalAmountMinor: number;
  readonly currency: string;
}

export interface DailyTransactionReport {
  readonly date: string;
  readonly totalTransactions: number;
  readonly totalAmountMinor: number;
  readonly rows: readonly DailyTransactionRow[];
  readonly generatedAt: string;
  readonly stub: boolean;
}

// ---------------------------------------------------------------------------
// Delivery SLA
// ---------------------------------------------------------------------------

export interface DeliverySLARow {
  readonly serviceClass: string;
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly onTime: number;
  readonly late: number;
  readonly failed: number;
  readonly onTimePct: number;
}

export interface DeliverySLAReport {
  readonly from: string;
  readonly to: string;
  readonly totalItems: number;
  readonly onTimePct: number;
  readonly rows: readonly DeliverySLARow[];
  readonly generatedAt: string;
  readonly stub: boolean;
}

// ---------------------------------------------------------------------------
// Fleet Ops
// ---------------------------------------------------------------------------

export interface FleetOpsReport {
  readonly period: string;
  readonly totalVehicles: number;
  readonly activeVehicles: number;
  readonly utilizationPct: number;
  readonly maintenanceFlagged: number;
  readonly downtimeHours: number;
  readonly rows: readonly { readonly vehicleId: string; readonly driverId: string; readonly trips: number; readonly utilization: number; readonly maintained: boolean }[];
  readonly generatedAt: string;
  readonly stub: true;
}

// ---------------------------------------------------------------------------
// KYC Compliance
// ---------------------------------------------------------------------------

export interface KYCComplianceRow {
  readonly tier: string;
  readonly submitted: number;
  readonly approved: number;
  readonly rejected: number;
  readonly pending: number;
  readonly approvalPct: number;
}

export interface KYCComplianceReport {
  readonly period: string;
  readonly totalRequests: number;
  readonly approvedCount: number;
  readonly rejectedCount: number;
  readonly pendingCount: number;
  readonly rows: readonly KYCComplianceRow[];
  readonly generatedAt: string;
  readonly stub: boolean;
}

// ---------------------------------------------------------------------------
// Audit Compliance
// ---------------------------------------------------------------------------

export interface AuditComplianceRow {
  readonly entityType: string;
  readonly action: string;
  readonly count: number;
}

export interface AuditComplianceReport {
  readonly period: string;
  readonly totalEvents: number;
  readonly uniqueActors: number;
  readonly rows: readonly AuditComplianceRow[];
  readonly generatedAt: string;
  readonly stub: boolean;
}

// ---------------------------------------------------------------------------
// Reconciliation
// ---------------------------------------------------------------------------

export interface ReconciliationReport {
  readonly period: string;
  readonly channels: readonly { readonly channel: string; readonly ledgerMinor: number; readonly settledMinor: number; readonly varianceMinor: number }[];
  readonly totalLedgerMinor: number;
  readonly totalSettledMinor: number;
  readonly netVarianceMinor: number;
  readonly generatedAt: string;
  readonly stub: true;
}

// ---------------------------------------------------------------------------
// Revenue Sharing
// ---------------------------------------------------------------------------

export interface RevenueSharingReport {
  readonly period: string;
  readonly franchiseId: string | null;
  readonly lines: readonly { readonly service: string; readonly revenueMinor: number; readonly commissionPct: number; readonly payoutMinor: number }[];
  readonly totalPayoutMinor: number;
  readonly generatedAt: string;
  readonly stub: true;
}

// ---------------------------------------------------------------------------
// Staff Productivity
// ---------------------------------------------------------------------------

export interface StaffProductivityRow {
  readonly staffId: string | null;
  readonly actionCount: number;
  readonly entityTypes: readonly string[];
}

export interface StaffProductivityReport {
  readonly period: string;
  readonly totalActions: number;
  readonly uniqueStaff: number;
  readonly rows: readonly StaffProductivityRow[];
  readonly generatedAt: string;
  readonly stub: boolean;
}

// ---------------------------------------------------------------------------
// Government Services Monthly
// ---------------------------------------------------------------------------

export interface GovServicesMonthlyRow {
  readonly agency: string;
  readonly submitted: number;
  readonly completed: number;
  readonly rejected: number;
  readonly completionPct: number;
}

export interface GovServicesMonthlyReport {
  readonly month: string;
  readonly totalRequests: number;
  readonly completedRequests: number;
  readonly rows: readonly GovServicesMonthlyRow[];
  readonly generatedAt: string;
  readonly stub: boolean;
}

// ---------------------------------------------------------------------------
// Franchise Scorecard
// ---------------------------------------------------------------------------

export interface FranchiseScorecardRow {
  readonly franchiseId: string;
  readonly mailItemCount: number;
  readonly onTimeDeliveryPct: number;
  readonly kpiScore: number;
  readonly tier: string;
  readonly incentiveEligible: boolean;
}

export interface FranchiseScorecardReport {
  readonly period: string;
  readonly franchiseId: string | null;
  readonly totalFranchises: number;
  readonly avgKpiScore: number;
  readonly rows: readonly FranchiseScorecardRow[];
  readonly generatedAt: string;
  readonly stub: boolean;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export async function getPreBuiltReportIndex(): Promise<readonly ReportIndexItem[]> {
  return request<readonly ReportIndexItem[]>(apiBase(), '/v1/reports', { method: 'GET' });
}

export async function getDailyTransactionReport(
  date?: string,
): Promise<DailyTransactionReport> {
  return request<DailyTransactionReport>(apiBase(), '/v1/reports/daily-transactions', {
    method: 'GET',
    query: date ? { date } : undefined,
  });
}

export async function getDeliverySLAReport(opts: {
  from?: string;
  to?: string;
  serviceClass?: string;
}): Promise<DeliverySLAReport> {
  const query: Record<string, string> = {};
  if (opts.from) query['from'] = opts.from;
  if (opts.to) query['to'] = opts.to;
  if (opts.serviceClass) query['serviceClass'] = opts.serviceClass;
  return request<DeliverySLAReport>(apiBase(), '/v1/reports/delivery-sla', {
    method: 'GET',
    query: Object.keys(query).length > 0 ? query : undefined,
  });
}

export async function getFleetOpsReport(period?: string): Promise<FleetOpsReport> {
  return request<FleetOpsReport>(apiBase(), '/v1/reports/fleet-ops', {
    method: 'GET',
    query: period ? { period } : undefined,
  });
}

export async function getKYCComplianceReport(period?: string): Promise<KYCComplianceReport> {
  return request<KYCComplianceReport>(apiBase(), '/v1/reports/kyc-compliance', {
    method: 'GET',
    query: period ? { period } : undefined,
  });
}

export async function getAuditComplianceReport(opts: {
  period?: string;
  actor?: string;
}): Promise<AuditComplianceReport> {
  const query: Record<string, string> = {};
  if (opts.period) query['period'] = opts.period;
  if (opts.actor) query['actor'] = opts.actor;
  return request<AuditComplianceReport>(apiBase(), '/v1/reports/audit-compliance', {
    method: 'GET',
    query: Object.keys(query).length > 0 ? query : undefined,
  });
}

export async function getReconciliationReport(period?: string): Promise<ReconciliationReport> {
  return request<ReconciliationReport>(apiBase(), '/v1/reports/reconciliation', {
    method: 'GET',
    query: period ? { period } : undefined,
  });
}

export async function getRevenueSharingReport(opts: {
  franchiseId?: string;
  period?: string;
}): Promise<RevenueSharingReport> {
  const query: Record<string, string> = {};
  if (opts.franchiseId) query['franchiseId'] = opts.franchiseId;
  if (opts.period) query['period'] = opts.period;
  return request<RevenueSharingReport>(apiBase(), '/v1/reports/revenue-sharing', {
    method: 'GET',
    query: Object.keys(query).length > 0 ? query : undefined,
  });
}

export async function getStaffProductivityReport(opts: {
  period?: string;
  staffId?: string;
}): Promise<StaffProductivityReport> {
  const query: Record<string, string> = {};
  if (opts.period) query['period'] = opts.period;
  if (opts.staffId) query['staffId'] = opts.staffId;
  return request<StaffProductivityReport>(apiBase(), '/v1/reports/staff-productivity', {
    method: 'GET',
    query: Object.keys(query).length > 0 ? query : undefined,
  });
}

export async function getGovServicesMonthlyReport(month?: string): Promise<GovServicesMonthlyReport> {
  return request<GovServicesMonthlyReport>(apiBase(), '/v1/reports/gov-services-monthly', {
    method: 'GET',
    query: month ? { month } : undefined,
  });
}

export async function getFranchiseScorecardReport(opts: {
  franchiseId?: string;
  period?: string;
}): Promise<FranchiseScorecardReport> {
  const query: Record<string, string> = {};
  if (opts.franchiseId) query['franchiseId'] = opts.franchiseId;
  if (opts.period) query['period'] = opts.period;
  return request<FranchiseScorecardReport>(apiBase(), '/v1/reports/franchise-scorecard', {
    method: 'GET',
    query: Object.keys(query).length > 0 ? query : undefined,
  });
}
